/**
 * WaitlistManager - Admin component for managing waitlist/interest list entries
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Clock, Users, Calendar, Mail, Phone, Search, X, Plus, Trash2,
  CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp,
  ArrowRight, FileText, RefreshCw, Filter, RotateCcw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WaitlistManager = ({ open, onOpenChange }) => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, waiting: 0, invited: 0, paid: 0, expired: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [addToClassEntry, setAddToClassEntry] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [selectedStudentIndices, setSelectedStudentIndices] = useState([]);
  const [includeTravelFee, setIncludeTravelFee] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fetchWaitlist = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/api/waitlist?${params.toString()}`);
      setEntries(response.data.entries || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchApprovedBookings = async (classTypeIds) => {
    setLoadingBookings(true);
    try {
      const response = await axios.get(`${API}/api/bookings`, {
        params: { status: 'approved', limit: 50 }
      });
      
      // Filter to upcoming classes that match the waitlist class types
      const now = new Date();
      const filtered = (response.data || []).filter(b => {
        const startTime = new Date(b.start_time);
        return startTime > now && classTypeIds.includes(b.class_type_id);
      });
      
      setBookings(filtered);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load available classes');
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchWaitlist();
    }
  }, [open, fetchWaitlist]);

  const handleAddToClass = async () => {
    if (!addToClassEntry || !selectedBookingId) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/api/waitlist/${addToClassEntry.id}/add-to-class`, {
        booking_id: selectedBookingId,
        student_indices: selectedStudentIndices.length > 0 ? selectedStudentIndices : null,
        include_travel_fee: includeTravelFee
      });
      
      toast.success(response.data.message || 'Students added to class!');
      setAddToClassEntry(null);
      setSelectedBookingId('');
      setSelectedStudentIndices([]);
      fetchWaitlist();
    } catch (error) {
      console.error('Failed to add to class:', error);
      toast.error(error.response?.data?.detail || 'Failed to add students to class');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this waitlist entry?')) return;
    
    try {
      await axios.delete(`${API}/api/waitlist/${entryId}`);
      toast.success('Entry deleted');
      fetchWaitlist();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleCancelInvite = async (entryId, keepOnWaitlist) => {
    const action = keepOnWaitlist ? 'return to waitlist' : 'remove completely';
    if (!window.confirm(`Are you sure you want to cancel this invite and ${action}?`)) return;
    
    try {
      const response = await axios.post(`${API}/api/waitlist/${entryId}/cancel-invite`, {
        keep_on_waitlist: keepOnWaitlist
      });
      toast.success(response.data.message || 'Invite cancelled');
      fetchWaitlist();
    } catch (error) {
      console.error('Failed to cancel invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel invite');
    }
  };

  const handleUpdateNotes = async (entryId, notes) => {
    try {
      await axios.put(`${API}/api/waitlist/${entryId}`, { admin_notes: notes });
      toast.success('Notes saved');
      fetchWaitlist();
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'waiting':
        return <Badge className="bg-yellow-600/30 text-yellow-300 border-yellow-600">Waiting</Badge>;
      case 'invited':
        return <Badge className="bg-blue-600/30 text-blue-300 border-blue-600">Invited</Badge>;
      case 'paid':
        return <Badge className="bg-green-600/30 text-green-300 border-green-600">Paid</Badge>;
      case 'expired':
        return <Badge className="bg-red-600/30 text-red-300 border-red-600">Expired</Badge>;
      case 'removed':
        return <Badge className="bg-slate-600/30 text-slate-300 border-slate-600">Removed</Badge>;
      default:
        return <Badge className="bg-slate-600/30 text-slate-300">{status}</Badge>;
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.contact_email?.toLowerCase().includes(search) ||
      entry.students?.some(s => 
        s.first_name?.toLowerCase().includes(search) ||
        s.last_name?.toLowerCase().includes(search) ||
        s.email?.toLowerCase().includes(search)
      ) ||
      entry.class_types?.some(ct => ct.name?.toLowerCase().includes(search))
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-slate-900 text-white border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Clock className="h-6 w-6 text-pink-400" />
            Waitlist Manager
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Manage students waiting for class availability
          </DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3 mb-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
              <p className="text-xs text-slate-400">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-900/30 border-yellow-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-yellow-300">{stats.waiting || 0}</p>
              <p className="text-xs text-yellow-400">Waiting</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/30 border-blue-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-300">{stats.invited || 0}</p>
              <p className="text-xs text-blue-400">Invited</p>
            </CardContent>
          </Card>
          <Card className="bg-green-900/30 border-green-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-300">{stats.paid || 0}</p>
              <p className="text-xs text-green-400">Paid</p>
            </CardContent>
          </Card>
          <Card className="bg-red-900/30 border-red-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-300">{stats.expired || 0}</p>
              <p className="text-xs text-red-400">Expired</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or class type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-slate-800 border-slate-600 text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white"
          >
            <option value="">All Status</option>
            <option value="waiting">Waiting</option>
            <option value="invited">Invited</option>
            <option value="paid">Paid</option>
            <option value="expired">Expired</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchWaitlist}
            className="border-slate-600 text-white"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Entries List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-pink-400" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No waitlist entries found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEntries.map(entry => (
              <Card key={entry.id} className={`bg-slate-800 border-slate-700 ${!entry.admin_seen ? 'ring-2 ring-red-500/50 border-red-500/50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {!entry.admin_seen && (
                          <Badge className="bg-red-500 text-white text-xs animate-pulse">NEW</Badge>
                        )}
                        {getStatusBadge(entry.status)}
                        <span className="text-sm text-slate-400">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-slate-500">
                          #{entry.id?.slice(0, 8).toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-white">
                          <Users className="h-4 w-4 text-pink-400" />
                          {entry.student_count || entry.students?.length || 0} student(s)
                        </span>
                        <span className="flex items-center gap-1 text-slate-300">
                          <Mail className="h-4 w-4 text-slate-400" />
                          {entry.contact_email}
                        </span>
                        {entry.needed_by_date && (
                          <span className="flex items-center gap-1 text-amber-300">
                            <Calendar className="h-4 w-4" />
                            Need by: {entry.needed_by_date}
                          </span>
                        )}
                      </div>
                      
                      <div className="mt-2 flex flex-wrap gap-1">
                        {entry.class_types?.map(ct => (
                          <Badge key={ct.id} variant="outline" className="text-xs border-slate-600 text-slate-300">
                            {ct.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {entry.status === 'waiting' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setAddToClassEntry(entry);
                            fetchApprovedBookings(entry.class_type_ids || []);
                          }}
                          className="bg-pink-600 hover:bg-pink-500 text-white"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add to Class
                        </Button>
                      )}
                      {entry.status === 'invited' && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelInvite(entry.id, true)}
                            className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                            title="Cancel invite but keep on waitlist"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Return to Waitlist
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelInvite(entry.id, false)}
                            className="border-red-600 text-red-400 hover:bg-red-900/30"
                            title="Cancel invite and remove from waitlist"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                        className="text-slate-400 hover:text-white"
                      >
                        {expandedEntry === entry.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      {entry.status !== 'invited' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedEntry === entry.id && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-pink-400 mb-2">Students</h4>
                          <div className="space-y-2">
                            {entry.students?.map((student, idx) => (
                              <div key={idx} className="bg-slate-700/50 rounded p-2 text-sm">
                                <p className="text-white font-medium">{student.first_name} {student.last_name}</p>
                                <p className="text-slate-400">{student.email}</p>
                                <p className="text-slate-400">{student.phone}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-pink-400 mb-2">Admin Notes</h4>
                          <textarea
                            className="w-full bg-slate-700 border border-slate-600 rounded p-2 text-white text-sm min-h-[80px]"
                            defaultValue={entry.admin_notes || ''}
                            placeholder="Add notes..."
                            onBlur={(e) => {
                              if (e.target.value !== (entry.admin_notes || '')) {
                                handleUpdateNotes(entry.id, e.target.value);
                              }
                            }}
                          />
                          {entry.customer_notes && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-400">Customer notes:</p>
                              <p className="text-sm text-slate-300 italic">"{entry.customer_notes}"</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {entry.invoice_id && (
                        <div className="mt-4 p-3 bg-blue-900/30 rounded-lg border border-blue-700">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-blue-400" />
                            <span className="text-sm text-blue-300">Invoice created</span>
                            {entry.invoice_expires_at && (
                              <span className="text-xs text-blue-400">
                                Expires: {new Date(entry.invoice_expires_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add to Class Modal */}
        {addToClassEntry && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="h-5 w-5 text-pink-400" />
                  Add to Class
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-400 mb-2">
                    Adding students from waitlist entry #{addToClassEntry.id?.slice(0, 8).toUpperCase()}
                  </p>
                </div>

                {/* Student Selection */}
                <div>
                  <label className="text-sm text-slate-300 block mb-2">Select students to add:</label>
                  <div className="space-y-2">
                    {addToClassEntry.students?.map((student, idx) => (
                      <label
                        key={idx}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                          selectedStudentIndices.length === 0 || selectedStudentIndices.includes(idx)
                            ? 'bg-pink-900/30 border-pink-600'
                            : 'bg-slate-800 border-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedStudentIndices.length === 0 || selectedStudentIndices.includes(idx)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // If previously had specific selection, add this one
                              if (selectedStudentIndices.length > 0) {
                                setSelectedStudentIndices([...selectedStudentIndices, idx]);
                              }
                              // If none selected (meaning all), keep as all
                            } else {
                              // Uncheck - if all were selected, now select all except this one
                              if (selectedStudentIndices.length === 0) {
                                const allExceptThis = addToClassEntry.students.map((_, i) => i).filter(i => i !== idx);
                                setSelectedStudentIndices(allExceptThis);
                              } else {
                                setSelectedStudentIndices(selectedStudentIndices.filter(i => i !== idx));
                              }
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-white">{student.first_name} {student.last_name}</span>
                        <span className="text-slate-400 text-sm">({student.email})</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Class Selection */}
                <div>
                  <label className="text-sm text-slate-300 block mb-2">Select class:</label>
                  {loadingBookings ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading available classes...
                    </div>
                  ) : bookings.length === 0 ? (
                    <p className="text-amber-400 text-sm">No upcoming approved classes match the waitlist class types.</p>
                  ) : (
                    <select
                      value={selectedBookingId}
                      onChange={(e) => setSelectedBookingId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white"
                    >
                      <option value="">Select a class...</option>
                      {bookings.map(b => (
                        <option key={b.id} value={b.id}>
                          {b.class_type_name} - {new Date(b.start_time).toLocaleDateString()} ({b.student_count || 0} students)
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Travel Fee Option */}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeTravelFee}
                    onChange={(e) => setIncludeTravelFee(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-300">Include travel fee in invoice</span>
                </label>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAddToClassEntry(null);
                      setSelectedBookingId('');
                      setSelectedStudentIndices([]);
                    }}
                    className="border-slate-600 text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddToClass}
                    disabled={!selectedBookingId || processing}
                    className="bg-pink-600 hover:bg-pink-500 text-white"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Add to Class & Send Invoice
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistManager;
